import React, { PureComponent } from 'react';
import classnames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import { Form, Radio, Switch, Input, Checkbox, message, Tooltip, Icon } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ColorPicker, UploadPicture, FormItemLayout, ConfigFormDecorator } from '../../tools';
import {
    MAX_PAGE_NAME_LENGTH,
    MAX_TITLE_LENGTH,
    MAX_QUICK_ENTRY_LABEL_LENGTH,
    MAX_QUICK_ENTRIES,
    MAX_TITLE_IMAGE_SIZE,
    getQuickEntryEnabled,
    getEnabledFunctionCount,
    getQuickEntryOrder,
    QUICK_ENTRY_DEFAULT_ORDER,
} from '../../../utils/top-nav';
import './index.less';

const FormItem = Form.Item;
const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;

const getStyleOptions = () => [
    { label: '标准', value: 'standard' },
    { label: '沉浸式', value: 'immersive' },
];

const getColorTypeOptions = () => [
    { label: '高斯模糊', value: 'blur' },
    { label: '纯色', value: 'solid' },
];

const QUICK_ENTRY_ICON_URLS = {
    search: 'https://hx-gs-pub.oss-cn-beijing.aliyuncs.com/logo/top/search.png',
    gold: 'https://hx-gs-pub.oss-cn-beijing.aliyuncs.com/logo/top/gold.png',
    store: 'https://hx-gs-pub.oss-cn-beijing.aliyuncs.com/logo/top/shop.png',
};

const QUICK_ENTRY_META = [
    { key: 'search', defaultLabel: '搜索', icon: 'search' },
    { key: 'gold', defaultLabel: '金价', icon: 'gold' },
    { key: 'store', defaultLabel: '门店', icon: 'store' },
];

class TopNavForm extends PureComponent<any> {
    state = {
        quickEntryDragKey: 0,
        quickEntryOrder: null as string[] | null,
    };

    syncQuickEntryOrderFromProps = (props = this.props) => {
        const order = getQuickEntryOrder(props.value || {});
        this.setState({ quickEntryOrder: order });
    };

    /**
     * 文字标题始终跟随页面名称，避免预览/保存出现标题与页面名称不一致。
     */
    static deriveFormValues(values, changedValues) {
        const nextValues = { ...values };
        if (nextValues.titleMode !== 'image' && nextValues.titleEnabled !== false) {
            const pageName = (nextValues.pageName || '').trim();
            if (
                changedValues.pageName !== undefined ||
                (pageName && nextValues.titleText !== nextValues.pageName)
            ) {
                nextValues.titleText = nextValues.pageName;
            }
        }
        return nextValues;
    }

    syncDerivedValuesToProfile = () => {
        const { value, onChange } = this.props;
        if (!value || !onChange) {
            return;
        }
        const nextValues = TopNavForm.deriveFormValues(cloneDeep(value), {});
        if (JSON.stringify(nextValues) !== JSON.stringify(value)) {
            onChange(nextValues);
        }
    };

    componentDidMount() {
        this.syncQuickEntryOrderFromProps();
        this.syncDerivedValuesToProfile();
    }

    componentWillReceiveProps(nextProps) {
        const { form, value } = this.props;
        const { value: nextValue } = nextProps;

        const currentOrder = getQuickEntryOrder(value || {});
        const nextOrder = getQuickEntryOrder(nextValue || {});
        if (JSON.stringify(currentOrder) !== JSON.stringify(nextOrder)) {
            this.setState({ quickEntryOrder: nextOrder });
        }

        if (nextValue?.style === 'standard' && nextValue?.colorType === 'blur') {
            form.setFieldsValue({ colorType: 'solid' });
        }

        if (nextValue?.quickEntryEnabled && nextValue?.titleAlign === 'left') {
            form.setFieldsValue({ titleAlign: 'center' });
        }

        // 搜索框位置随标题/快捷入口联动
        if (nextValue?.searchEnabled) {
            const targetAlign = nextValue?.titleEnabled
                ? nextValue.titleAlign || 'center'
                : nextValue?.quickEntryEnabled
                ? 'center'
                : null;
            if (targetAlign && nextValue.searchAlign !== targetAlign) {
                form.setFieldsValue({ searchAlign: targetAlign });
            }
        }
    }

    showQuickEntryMaxWarning = () => {
        message.warning('最多可同时配置 2 个快捷入口');
    };

    handleStyleChange = (e) => {
        if (e.target.value === 'standard') {
            this.props.form.setFieldsValue({ colorType: 'solid' });
        }
    };

    handleQuickEntryCheck = (key: string, checked: boolean) => {
        const { form } = this.props;
        const values = form.getFieldsValue();
        const quickEntries = values.quickEntries || [];
        if (checked) {
            if (quickEntries.length >= MAX_QUICK_ENTRIES) {
                this.showQuickEntryMaxWarning();
                return;
            }
            form.setFieldsValue({ quickEntries: [...quickEntries, key] });
        } else {
            form.setFieldsValue({
                quickEntries: quickEntries.filter((k) => k !== key),
            });
        }
    };

    handleQuickEntryLabelChange = (key: string, label: string) => {
        const { form } = this.props;
        const values = form.getFieldsValue();
        const labels = { ...(values.quickEntryLabels || {}) };
        labels[key] = label;
        form.setFieldsValue({ quickEntryLabels: labels });
    };

    renderDisabledSwitch(children, disabled) {
        if (!disabled) return children;
        return (
            <Tooltip title="“标题、搜索、快捷入口”最多只能同时开启2个">
                <span className="switch-tooltip-wrapper">{children}</span>
            </Tooltip>
        );
    }

    handleQuickEntryDragEnd = (result) => {
        const nextDragKey = Date.now();

        if (!result.destination) {
            this.setState({ quickEntryDragKey: nextDragKey });
            return;
        }

        const { form, onChange, value } = this.props;
        const order = [...(this.state.quickEntryOrder || getQuickEntryOrder(value || {}))];
        const [removed] = order.splice(result.source.index, 1);
        order.splice(result.destination.index, 0, removed);

        const nextValues = cloneDeep(value || form.getFieldsValue());
        nextValues.quickEntryOrder = order;

        this.setState({ quickEntryOrder: order, quickEntryDragKey: nextDragKey }, () => {
            form.setFieldsValue({ quickEntryOrder: order });
            onChange && onChange(nextValues);
        });
    };

    renderTitleSection(values, formItemLayout, titleDisabled, quickEntryEnabled) {
        const { getFieldDecorator } = this.props.form;
        const { sourceRoot, uploadAction } = this.props;
        return (
            <section className="editor-top-nav-section">
                <div className="section-title-row">
                    <span className="section-title">标题设置</span>
                    {this.renderDisabledSwitch(
                        getFieldDecorator('titleEnabled', { valuePropName: 'checked' })(
                            <Switch size="small" disabled={titleDisabled} />
                        ),
                        titleDisabled
                    )}
                </div>
                {values.titleEnabled && (
                    <div className="section-body">
                        <FormItem {...formItemLayout} label="内容形式">
                            {getFieldDecorator('titleMode')(
                                <RadioGroup className="segment-group">
                                    <RadioButton value="text">文字</RadioButton>
                                    <RadioButton value="image">图片</RadioButton>
                                </RadioGroup>
                            )}
                        </FormItem>
                        {values.titleMode === 'text' && (
                            <p className="section-hint title-text-hint">
                                选择“文字”时，展示页面名称
                            </p>
                        )}
                        {values.titleMode === 'image' && (
                            <div className="title-image-upload">
                                {getFieldDecorator('titleImage')(
                                    <UploadPicture
                                        sourceRoot={sourceRoot}
                                        uploadAction={uploadAction}
                                        uploadText="上传"
                                        maxSize={MAX_TITLE_IMAGE_SIZE}
                                        previewMode="hover-actions"
                                    />
                                )}
                                <p className="section-hint">
                                    建议 224x80 像素的 PNG 图片，不超过 2MB
                                </p>
                                {!values.titleImage && (
                                    <p className="title-image-error">请上传标题图片</p>
                                )}
                            </div>
                        )}
                        <FormItem {...formItemLayout} label="位置选择">
                            {getFieldDecorator('titleAlign')(
                                <RadioGroup className="segment-group">
                                    <RadioButton value="center">居中</RadioButton>
                                    <RadioButton value="left" disabled={quickEntryEnabled}>
                                        {quickEntryEnabled ? (
                                            <Tooltip title="开启快捷入口时无法居左">
                                                <span>居左</span>
                                            </Tooltip>
                                        ) : (
                                            '居左'
                                        )}
                                    </RadioButton>
                                </RadioGroup>
                            )}
                        </FormItem>
                    </div>
                )}
            </section>
        );
    }

    renderQuickEntryIcon(key: string) {
        const iconUrl = QUICK_ENTRY_ICON_URLS[key];
        if (iconUrl) {
            return <img className="quick-entry-icon" src={iconUrl} alt="" />;
        }
        return <Icon className="quick-entry-icon" type={key} />;
    }

    renderQuickEntrySection(values, quickEntryDisabled) {
        const { getFieldDecorator } = this.props.form;
        const quickEntryEnabled = getQuickEntryEnabled(values);
        const selectedEntries = values.quickEntries || [];
        const labels = values.quickEntryLabels || {};
        const order = this.state.quickEntryOrder || getQuickEntryOrder(values);
        const orderedMeta = order
            .map((key) => QUICK_ENTRY_META.find((item) => item.key === key))
            .filter(Boolean);

        return (
            <section className="editor-top-nav-section">
                <div className="section-title-row">
                    <span className="section-title">快捷入口设置</span>
                    {this.renderDisabledSwitch(
                        getFieldDecorator('quickEntryEnabled', {
                            valuePropName: 'checked',
                            initialValue: false,
                        })(<Switch size="small" disabled={quickEntryDisabled} />),
                        quickEntryDisabled
                    )}
                </div>
                <p className="section-hint">最多可同时配置2个快捷入口，拖拽可调整展示顺序</p>
                {getFieldDecorator('quickEntries', { initialValue: [] })(<span />)}
                {getFieldDecorator('quickEntryLabels', { initialValue: {} })(<span />)}
                {getFieldDecorator('quickEntryOrder', {
                    initialValue: [...QUICK_ENTRY_DEFAULT_ORDER],
                })(<span />)}
                {quickEntryEnabled && (
                    <div className="section-body">
                        <DragDropContext
                            key={this.state.quickEntryDragKey}
                            onDragEnd={this.handleQuickEntryDragEnd}
                        >
                            <Droppable droppableId="quickEntryList" direction="vertical">
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="quick-entry-list"
                                    >
                                        {orderedMeta.map((entry, index) => {
                                            const checked = selectedEntries.includes(entry.key);
                                            const canCheck =
                                                checked ||
                                                selectedEntries.length < MAX_QUICK_ENTRIES;
                                            const label = labels[entry.key] || '';
                                            return (
                                                <Draggable
                                                    key={entry.key}
                                                    draggableId={entry.key}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={classnames(
                                                                'quick-entry-item',
                                                                {
                                                                    'quick-entry-item--dragging':
                                                                        snapshot.isDragging,
                                                                }
                                                            )}
                                                        >
                                                            <div className="quick-entry-row">
                                                                <span
                                                                    className="quick-entry-drag-handle"
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <Icon type="menu" />
                                                                </span>
                                                                {this.renderQuickEntryIcon(
                                                                    entry.key
                                                                )}
                                                                <span className="quick-entry-name">
                                                                    {entry.defaultLabel}
                                                                </span>
                                                                <Input
                                                                    className="quick-entry-label-input"
                                                                    value={label}
                                                                    maxLength={
                                                                        MAX_QUICK_ENTRY_LABEL_LENGTH
                                                                    }
                                                                    placeholder="2个字"
                                                                    onChange={(e) =>
                                                                        this.handleQuickEntryLabelChange(
                                                                            entry.key,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                                <Checkbox
                                                                    checked={checked}
                                                                    disabled={!canCheck}
                                                                    onChange={(e) =>
                                                                        this.handleQuickEntryCheck(
                                                                            entry.key,
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                )}
            </section>
        );
    }

    renderSearchSection(values, searchDisabled, formItemLayout) {
        const { getFieldDecorator } = this.props.form;
        const quickEntryEnabled = getQuickEntryEnabled(values);
        const titleEnabled = values.titleEnabled;
        const searchAlignLocked = titleEnabled || quickEntryEnabled;
        let searchHint = '';
        if (values.searchEnabled) {
            if (titleEnabled) {
                searchHint = '搜索框位置随标题位置自动适配';
            } else if (quickEntryEnabled) {
                searchHint = '与快捷入口同时开启时，搜索框默认居中';
            }
        }

        return (
            <section className="editor-top-nav-section">
                <div className="section-title-row">
                    <span className="section-title">搜索框设置</span>
                    {this.renderDisabledSwitch(
                        getFieldDecorator('searchEnabled', { valuePropName: 'checked' })(
                            <Switch size="small" disabled={searchDisabled} />
                        ),
                        searchDisabled
                    )}
                </div>
                {values.searchEnabled && (
                    <div className="section-body">
                        {searchHint && <p className="section-hint">{searchHint}</p>}
                        {!searchAlignLocked && (
                            <FormItem {...formItemLayout} label="搜索框位置">
                                {getFieldDecorator('searchAlign')(
                                    <RadioGroup className="segment-group">
                                        <RadioButton value="center">居中</RadioButton>
                                        <RadioButton value="left">居左</RadioButton>
                                    </RadioGroup>
                                )}
                            </FormItem>
                        )}
                    </div>
                )}
            </section>
        );
    }

    render() {
        const {
            form: { getFieldDecorator },
            value,
        } = this.props;
        const values = value || {};
        const formItemLayout = FormItemLayout;
        const functionCount = getEnabledFunctionCount(values);
        const titleDisabled = !values.titleEnabled && functionCount >= 2;
        const searchDisabled = !values.searchEnabled && functionCount >= 2;
        const quickEntryDisabled = !getQuickEntryEnabled(values) && functionCount >= 2;
        const quickEntryEnabled = getQuickEntryEnabled(values);
        const isImmersive = values.style === 'immersive';

        return (
            <Form className="editor-top-nav">
                <section className="editor-top-nav-section">
                    <FormItem {...formItemLayout} label="页面名称">
                        {getFieldDecorator('pageName', {
                            rules: [
                                {
                                    required: true,
                                    whitespace: true,
                                    message: '页面名称不能为空',
                                },
                                {
                                    max: MAX_PAGE_NAME_LENGTH,
                                    message: '页面名称最多 10 个字',
                                },
                            ],
                        })(<Input maxLength={MAX_PAGE_NAME_LENGTH} placeholder="请输入页面名称" />)}
                    </FormItem>
                </section>

                <section className="editor-top-nav-section">
                    <div className="section-title">风格设置</div>
                    <div className="section-body">
                        <FormItem {...formItemLayout} label="风格选择">
                            {getFieldDecorator('style')(
                                <RadioGroup
                                    className="segment-group"
                                    onChange={this.handleStyleChange}
                                >
                                    {getStyleOptions().map((opt) => (
                                        <RadioButton key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </RadioButton>
                                    ))}
                                </RadioGroup>
                            )}
                        </FormItem>
                    </div>
                </section>

                {this.renderTitleSection(values, formItemLayout, titleDisabled, quickEntryEnabled)}

                {this.renderSearchSection(values, searchDisabled, formItemLayout)}

                {this.renderQuickEntrySection(values, quickEntryDisabled)}

                <section className="editor-top-nav-section">
                    <div className="section-title">颜色设置</div>
                    <div className="section-body">
                        <FormItem {...formItemLayout} label="颜色类型">
                            {getFieldDecorator('colorType')(
                                <RadioGroup className="segment-group">
                                    {getColorTypeOptions().map((opt) => {
                                        const isBlur = opt.value === 'blur';
                                        const disabled = isBlur && !isImmersive;
                                        return (
                                            <RadioButton
                                                key={opt.value}
                                                value={opt.value}
                                                disabled={disabled}
                                            >
                                                {disabled ? (
                                                    <Tooltip title="仅沉浸式风格可选选择">
                                                        <span>{opt.label}</span>
                                                    </Tooltip>
                                                ) : (
                                                    opt.label
                                                )}
                                            </RadioButton>
                                        );
                                    })}
                                </RadioGroup>
                            )}
                        </FormItem>
                        {values.colorType !== 'blur' && (
                            <FormItem {...formItemLayout} label="背景颜色" className="color-item">
                                {getFieldDecorator('bgColor')(
                                    <ColorPicker defaultColor="#ffffff" />
                                )}
                            </FormItem>
                        )}
                        <FormItem {...formItemLayout} label="文字颜色">
                            {getFieldDecorator('textColor')(
                                <RadioGroup className="segment-group">
                                    <RadioButton value="black">黑色</RadioButton>
                                    <RadioButton value="white">白色</RadioButton>
                                </RadioGroup>
                            )}
                        </FormItem>
                    </div>
                </section>
            </Form>
        );
    }
}

export const TopNav = ConfigFormDecorator(TopNavForm);
